/**
 * LP Template JavaScript (Refresh-stable Version)
 * - Google Fonts loader (no duplicates)
 * - Slash "/" -> <br> conversion
 * - Shopify editor section reload supported
 * - IMPORTANT: ScrollTrigger refresh after fonts/images/layout changes
 */

(function () {
  'use strict';

  /* ===============================
     Helpers
  =============================== */
  function checkGSAP() {
    return typeof gsap !== 'undefined' && typeof ScrollTrigger !== 'undefined';
  }

  function getSectionIdFromWrapper(wrapper) {
    if (!wrapper || !wrapper.id || !wrapper.id.startsWith('lp-template-')) return null;
    return wrapper.id.replace('lp-template-', '');
  }

  function lazyInit(el, cb) {
    if (!el) return;
    const io = new IntersectionObserver(
      entries => {
        entries.forEach(e => {
          if (e.isIntersecting) {
            cb();
            io.disconnect();
          }
        });
      },
      { rootMargin: '200px' }
    );
    io.observe(el);
  }

  function escapeHtml(str) {
    return String(str)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  }

  function hashString(str) {
    let h = 5381;
    for (let i = 0; i < str.length; i++) h = (h * 33) ^ str.charCodeAt(i);
    return (h >>> 0).toString(16);
  }

  function debounce(fn, wait) {
    let t;
    return function () {
      clearTimeout(t);
      const args = arguments;
      t = setTimeout(() => fn.apply(null, args), wait);
    };
  }

  /* ===============================
     ScrollTrigger refresh safety
  =============================== */
  const refreshScrollTriggers = debounce(() => {
    if (!checkGSAP()) return;
    try {
      ScrollTrigger.refresh(true);
    } catch (e) {
      // noop
    }
  }, 120);

  function refreshSoon() {
    // two-phase refresh: next frame + small delay (fonts/images settle)
    requestAnimationFrame(() => refreshScrollTriggers());
    setTimeout(() => refreshScrollTriggers(), 250);
  }

  /* ===============================
     Google Fonts Loader
  =============================== */
  function ensurePreconnectOnce() {
    const key = 'lpGoogleFontsPreconnect';
    if (document.documentElement.dataset[key] === '1') return;
    document.documentElement.dataset[key] = '1';

    const head = document.head || document.getElementsByTagName('head')[0];
    if (!head) return;

    const pre1 = document.createElement('link');
    pre1.rel = 'preconnect';
    pre1.href = 'https://fonts.googleapis.com';
    head.appendChild(pre1);

    const pre2 = document.createElement('link');
    pre2.rel = 'preconnect';
    pre2.href = 'https://fonts.gstatic.com';
    pre2.crossOrigin = 'anonymous';
    head.appendChild(pre2);
  }

  function ensureGoogleFontLoaded(url) {
    if (!url) return;
    const cleaned = String(url).trim();
    if (!cleaned || cleaned === '-') return;
    if (!/^https:\/\/fonts\.googleapis\.com\//i.test(cleaned)) return;

    ensurePreconnectOnce();

    const head = document.head || document.getElementsByTagName('head')[0];
    if (!head) return;

    const id = 'lp-google-font-' + hashString(cleaned);
    if (document.getElementById(id)) return;

    const link = document.createElement('link');
    link.id = id;
    link.rel = 'stylesheet';
    link.href = cleaned;

    // fonts load -> layout changes -> refresh ScrollTrigger
    link.addEventListener('load', refreshSoon, { once: true });
    head.appendChild(link);
  }

  function initGoogleFonts(wrapper) {
    if (!wrapper) return;
    const primaryUrl = wrapper.getAttribute('data-lp-font-primary-url') || '';
    const secondaryUrl = wrapper.getAttribute('data-lp-font-secondary-url') || '';
    ensureGoogleFontLoaded(primaryUrl);
    ensureGoogleFontLoaded(secondaryUrl);
  }

  /* ===============================
     Slash "/" -> <br> converter
  =============================== */
  function convertBreaksInElement(el) {
    if (!el || el.dataset.lpBreakProcessed === '1') return;

    const hasComplexElements = Array.from(el.childNodes).some(
      n => n.nodeType === 1 && n.nodeName !== 'BR'
    );
    if (hasComplexElements) return;

    let text = el.textContent || '';
    text = text.replace(/<br\s*\/?>/gi, '\n');

    const hasAnyBreak =
      text.includes('/') || text.includes('\n') || text.includes('\r');

    if (!hasAnyBreak) {
      el.dataset.lpBreakProcessed = '1';
      return;
    }

    const parts = text
      .split(/(?:\r?\n)+|(?<![A-Za-z0-9._%+-])\s*\/\s*|\s*\/\s*(?![A-Za-z0-9._%+-])/g)
      .map(s => s.trim())
      .filter(Boolean);

    if (!parts.length) {
      el.dataset.lpBreakProcessed = '1';
      return;
    }

    el.innerHTML = parts.map(escapeHtml).join('<br>');
    el.dataset.lpBreakProcessed = '1';
  }

  function convertAllBreaks(wrapper) {
    if (!wrapper) return;
    wrapper.querySelectorAll('.lp-break').forEach(convertBreaksInElement);
  }

  /* ===============================
     Scroll Indicator
  =============================== */
  function initScrollIndicator(wrapper) {
    const indicator = wrapper.querySelector('.lp-simple-scroll');
    const hero = wrapper.querySelector('.lp-hero');
    if (!indicator || !hero) return;

    if (typeof wrapper._lpScrollIndicatorCleanup === 'function') {
      wrapper._lpScrollIndicatorCleanup();
      wrapper._lpScrollIndicatorCleanup = null;
    }

    function update() {
      indicator.classList.toggle(
        'hidden',
        hero.getBoundingClientRect().bottom < window.innerHeight * 0.3
      );
    }

    window.addEventListener('scroll', update, { passive: true });
    update();

    wrapper._lpScrollIndicatorCleanup = () => {
      window.removeEventListener('scroll', update);
    };
  }

  /* ===============================
     GSAP Cleanup (avoid duplicates)
  =============================== */
  function killScrollTriggersInside(wrapper) {
    if (!checkGSAP()) return;
    try {
      ScrollTrigger.getAll().forEach(st => {
        const triggerEl = st && st.trigger;
        if (triggerEl && wrapper.contains(triggerEl)) st.kill();
      });
    } catch (e) {
      // noop
    }
  }

  /* ===============================
     Hero animations
  =============================== */
  function initHeroAnimations(wrapper, sectionId) {
    if (!checkGSAP()) return;
    const hero = wrapper.querySelector(`#lp-hero-${sectionId}`);
    if (!hero) return;

    const title = hero.querySelector('.lp-hero__title');
    const subtitle = hero.querySelector('.lp-hero__subtitle');
    const imageWrap = hero.querySelector('.lp-hero__image-container');
    const image = hero.querySelector('.lp-hero__image');

    gsap.set([title, subtitle, imageWrap], { opacity: 0, y: 40 });

    gsap
      .timeline({ delay: 0.15 })
      .to(title, { opacity: 1, y: 0, duration: 0.8 })
      .to(subtitle, { opacity: 1, y: 0, duration: 0.6 }, '-=0.4')
      .to(imageWrap, { opacity: 1, y: 0, duration: 0.9 }, '-=0.5');

    if (image) {
      gsap.to(image, {
        scale: 1.12,
        y: -20,
        scrollTrigger: {
          trigger: hero,
          start: 'top top',
          end: 'bottom top',
          scrub: 0.6,
          invalidateOnRefresh: true
        }
      });

      // image load affects hero height -> refresh
      if (!image.complete) {
        image.addEventListener('load', refreshSoon, { once: true });
      }
    }
  }

  /* ===============================
     Statement / Text
  =============================== */
  function initStatementAnimations(wrapper) {
    if (!checkGSAP()) return;
    const section = wrapper.querySelector('.lp-statement');
    if (!section) return;

    lazyInit(section, () => {
      section.querySelectorAll('.reveal-text').forEach(el => {
        gsap.from(el, {
          opacity: 0,
          y: 40,
          duration: 0.7,
          ease: 'power2.out',
          scrollTrigger: {
            trigger: el,
            start: 'top 85%',
            once: true
          }
        });
      });
    });
  }

  /* ===============================
     Factory Story
  =============================== */
  function initFactoryAnimations(wrapper) {
    if (!checkGSAP()) return;
    const section = wrapper.querySelector('.lp-factory-story');
    if (!section) return;

    lazyInit(section, () => {
      section.querySelectorAll('.lp-factory__story-block').forEach(block => {
        const targets = [
          block.querySelector('.lp-factory__content'),
          block.querySelector('.lp-factory__image')
        ].filter(Boolean);
        if (!targets.length) return;

        gsap.from(targets, {
          opacity: 0,
          y: 40,
          duration: 0.8,
          ease: 'power2.out',
          stagger: 0.12,
          scrollTrigger: {
            trigger: block,
            start: 'top 85%',
            once: true
          }
        });
      });
    });
  }

  /* ===============================
     Features
  =============================== */
  function initFeaturesAnimations(wrapper) {
    if (!checkGSAP()) return;

    wrapper.querySelectorAll('.lp-feature-item').forEach((item, i) => {
      lazyInit(item, () => {
        const img = item.querySelector('.lp-feature-item__image-wrap');
        const text = item.querySelector('.lp-feature-item__text');

        gsap.from([img, text], {
          opacity: 0,
          x: i % 2 === 0 ? 40 : -40,
          duration: 0.8,
          ease: 'power2.out',
          stagger: 0.1,
          scrollTrigger: {
            trigger: item,
            start: 'top 85%',
            once: true
          }
        });
      });
    });
  }

  /* ===============================
     Gallery
  =============================== */
  function initGalleryAnimations(wrapper) {
    if (!checkGSAP() || window.innerWidth <= 768) return;
    const section = wrapper.querySelector('.lp-gallery-scroll');
    if (!section) return;

    lazyInit(section, () => {
      gsap.from(section.querySelectorAll('.lp-gallery__item'), {
        opacity: 0,
        y: 30,
        duration: 0.6,
        stagger: 0.15,
        scrollTrigger: {
          trigger: section,
          start: 'top 80%',
          once: true
        }
      });
    });
  }

  /* ===============================
     CTA
  =============================== */
  function initCTAAnimations(wrapper, sectionId) {
    if (!checkGSAP()) return;
    const cta = wrapper.querySelector(`#lp-cta-${sectionId}`);
    if (!cta) return;

    lazyInit(cta, () => {
      gsap.from(cta.querySelector('.lp-cta__container'), {
        opacity: 0,
        y: 60,
        duration: 0.8,
        ease: 'power2.out',
        scrollTrigger: {
          trigger: cta,
          start: 'top 80%',
          once: true
        }
      });
    });
  }

  /* ===============================
     Init
  =============================== */
  let gsapRegistered = false;
  function registerGSAPOnce() {
    if (gsapRegistered) return;
    if (!checkGSAP()) return;
    gsap.registerPlugin(ScrollTrigger);
    gsapRegistered = true;
  }

  function initWrapper(wrapper) {
    const sectionId = getSectionIdFromWrapper(wrapper);
    if (!sectionId) return;

    initGoogleFonts(wrapper);
    convertAllBreaks(wrapper);

    if (typeof wrapper._lpScrollIndicatorCleanup === 'function') {
      wrapper._lpScrollIndicatorCleanup();
      wrapper._lpScrollIndicatorCleanup = null;
    }

    // if reloaded in editor, kill old triggers
    if (wrapper.dataset.lpInited === '1') {
      killScrollTriggersInside(wrapper);
    }
    wrapper.dataset.lpInited = '1';

    initScrollIndicator(wrapper);

    if (checkGSAP()) {
      registerGSAPOnce();
      initHeroAnimations(wrapper, sectionId);
      initStatementAnimations(wrapper);
      initFeaturesAnimations(wrapper);
      initGalleryAnimations(wrapper);
      initFactoryAnimations(wrapper);
      initCTAAnimations(wrapper, sectionId);

      // after initial layout changes
      refreshSoon();
    }

    // if any images inside wrapper load later, refresh (safety)
    wrapper.querySelectorAll('img').forEach(img => {
      if (!img.complete) img.addEventListener('load', refreshSoon, { once: true });
    });
  }

  function initAll(root) {
    const scope = root || document;
    scope
      .querySelectorAll('[id^="lp-template-"].lp-template-wrapper')
      .forEach(initWrapper);
  }

  function boot() {
    initAll(document);

    // refresh on resize (desktop layout changes are big)
    window.addEventListener('resize', refreshSoon, { passive: true });

    // Shopify theme editor: re-init when section is loaded/re-rendered
    document.addEventListener('shopify:section:load', e => initAll(e.target));
  }

  document.readyState === 'loading'
    ? document.addEventListener('DOMContentLoaded', boot)
    : boot();
})();
